import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-black border border-white/15 px-4 py-3.5 text-white placeholder:text-white/30 focus:border-ad-red focus:outline-none transition-colors";

  return (
    <div className="min-h-screen grid place-items-center bg-ad-black px-5 grain">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md bg-ad-surface border border-white/10 p-8 sm:p-10"
      >
        <div className="flex items-center gap-3 text-ad-red mb-2">
          <Lock size={18} />
          <span className="text-xs font-bold uppercase tracking-[0.3em]">Admin Access</span>
        </div>
        <h1 className="font-display text-4xl uppercase tracking-tight mb-8">Sign In</h1>

        <form onSubmit={submit} className="flex flex-col gap-4" data-testid="admin-login-form">
          <input
            className={field}
            type="email"
            placeholder="Email"
            required
            data-testid="login-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            className={field}
            type="password"
            placeholder="Password"
            required
            data-testid="login-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && <p className="text-ad-red text-sm" data-testid="login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="mt-2 flex items-center justify-center gap-2 px-8 py-4 bg-ad-red text-white font-bold uppercase tracking-widest text-sm hover:bg-ad-redhover transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {loading ? "Signing In..." : "Sign In"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
